import { useMemo } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import type { RecentMaintenanceEntry } from "@/lib/wartungen";
import { coerceGradeValue, getGradeColor, type NormalizedField } from "../form/runtimeModel";
import { formatDate } from "../lib/wartungenUi";
import { AssetStatusBadge } from "./AssetStatusBadge";
import { buildMasterdataDisplayRows } from "./masterdataDisplay";
import type { HuelleAsset } from "./planModel";
import "./huelleDetail.css";

const RESULT_STATUS_LABELS: Record<string, string> = {
  open: "Offen",
  ok: "In Ordnung",
  defect_found: "Mangel",
  follow_up_required: "Nacharbeit",
  closed: "Abgeschlossen",
};

function formatResultStatus(value?: string | null): string {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return "—";
  return RESULT_STATUS_LABELS[key] || key;
}

function assetTitle(asset: HuelleAsset): string {
  const kennung = String(asset.external_id || "").trim();
  const name = String(asset.name || "").trim();
  if (kennung && name && name !== kennung) return `${kennung} · ${name}`;
  return kennung || name || `Objekt #${asset.asset_id}`;
}

function maintenanceDate(entry: RecentMaintenanceEntry): string {
  return formatDate(entry.completed_at ?? entry.updated_at ?? entry.performed_at ?? null);
}

function GradeBadge({ grade }: { grade: number | null }) {
  if (grade == null) {
    return <span className="text-xs text-label-tertiary">Keine Note</span>;
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white shadow-sm"
      style={{ backgroundColor: getGradeColor(grade) }}
    >
      Note {grade}
    </span>
  );
}

export function AssetStammdatenDialog({
  open,
  asset,
  masterdataFields,
  locationLabel,
  onClose,
  onOpenMaintenance,
  actions,
}: {
  open: boolean;
  asset: HuelleAsset | null;
  masterdataFields: NormalizedField[];
  locationLabel?: string;
  onClose: () => void;
  /** Öffnet eine abgeschlossene Wartung aus der Historie zum Ansehen. */
  onOpenMaintenance?: (entry: RecentMaintenanceEntry) => void;
  /** Aktionen (Wartung starten, Bearbeiten, Löschen) – erscheinen im Footer. */
  actions?: React.ReactNode;
}) {
  const masterdataRows = useMemo(() => {
    if (!asset) return [];
    const masterdata =
      asset.masterdata && typeof asset.masterdata === "object"
        ? (asset.masterdata as Record<string, unknown>)
        : {};
    return buildMasterdataDisplayRows(masterdata, masterdataFields);
  }, [asset, masterdataFields]);

  const recentMaintenances = useMemo(() => {
    if (!asset) return [];
    return Array.isArray(asset.recent_maintenances) ? asset.recent_maintenances : [];
  }, [asset]);

  const title = asset ? assetTitle(asset) : "Stammdaten";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={asset ? "Stammdaten, Status und letzte Wartungen dieses Objekts" : undefined}
      size="lg"
      plainBackdrop
      footer={
        <>
          <DialogCancelButton onClose={onClose} label="Schließen" />
          {actions ? <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div> : null}
        </>
      }
    >
      {asset ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl bg-fill-tertiary/50 px-3.5 py-3">
            <div className="min-w-0 space-y-1">
              <div className="font-mono text-sm font-semibold text-label">
                {String(asset.external_id || "").trim() || "—"}
              </div>
              <div className="text-sm text-label-secondary">
                {String(asset.name || "").trim() || "Ohne Bezeichnung"}
              </div>
              <div className="text-xs text-label-tertiary">
                {String(asset.location_label || locationLabel || "").trim() || "Standort"}
                {asset.status ? ` · ${String(asset.status)}` : ""}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <AssetStatusBadge asset={asset} size="md" />
            </div>
          </div>

          <section className="huelle-detail-section">
            <h3 className="huelle-detail-section__title">Stammdaten</h3>
            {masterdataRows.length ? (
              <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                {masterdataRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex items-baseline justify-between gap-3 border-b border-separator/50 py-1.5 last:border-b-0"
                  >
                    <dt className="text-sm text-label-secondary">{row.label}</dt>
                    <dd className="text-right text-sm font-medium text-label">{row.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-label-tertiary">Keine Stammdaten hinterlegt.</p>
            )}
          </section>

          <section className="huelle-detail-section">
            <h3 className="huelle-detail-section__title">Letzte Wartungen</h3>
            {recentMaintenances.length ? (
              <ul className="space-y-2">
                {recentMaintenances.map((entry, index) => {
                  const eventId = Number(entry.maintenance_event_id);
                  const grade = coerceGradeValue(entry.grade_final);
                  const typeName = String(
                    entry.maintenance_type_name || entry.maintenance_type_key || "",
                  ).trim();
                  const canOpen = Boolean(onOpenMaintenance) && Number.isFinite(eventId) && eventId > 0;
                  return (
                    <li key={canOpen ? eventId : `recent-${index}`}>
                      {canOpen ? (
                        <button
                          type="button"
                          className="huelle-history-item huelle-history-item--button"
                          onClick={() => onOpenMaintenance?.(entry)}
                        >
                          <div className="min-w-0 space-y-0.5 text-left">
                            <div className="text-sm font-medium text-label">{maintenanceDate(entry)}</div>
                            <div className="text-xs text-label-secondary">
                              {formatResultStatus(entry.result_status)}
                              {typeName ? ` · ${typeName}` : ""}
                            </div>
                          </div>
                          <span className="ml-auto flex items-center gap-2">
                            <GradeBadge grade={grade} />
                            <ChevronRightIcon className="h-4 w-4 text-label-tertiary" aria-hidden />
                          </span>
                        </button>
                      ) : (
                        <div className="huelle-history-item">
                          <div className="min-w-0 space-y-0.5">
                            <div className="text-sm font-medium text-label">{maintenanceDate(entry)}</div>
                            <div className="text-xs text-label-secondary">
                              {formatResultStatus(entry.result_status)}
                              {typeName ? ` · ${typeName}` : ""}
                            </div>
                          </div>
                          <GradeBadge grade={grade} />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-label-tertiary">Noch keine abgeschlossenen Wartungen.</p>
            )}
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}
